import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuggestionPanel = ({ 
  suggestions = [], 
  onAcceptSuggestion, 
  onRejectSuggestion,
  onAcceptAll,
  matchScore = 0,
  isLoading = false 
}) => {
  const [expandedSuggestions, setExpandedSuggestions] = useState(new Set());
  const [filter, setFilter] = useState('all'); // all, critical, recommended, bonus

  const toggleExpanded = (suggestionId) => {
    const newExpanded = new Set(expandedSuggestions);
    if (newExpanded?.has(suggestionId)) {
      newExpanded?.delete(suggestionId);
    } else {
      newExpanded?.add(suggestionId);
    }
    setExpandedSuggestions(newExpanded);
  };

  const filteredSuggestions = suggestions?.filter(suggestion => {
    if (filter === 'all') return true;
    return suggestion?.priority === filter;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-error';
      case 'recommended': return 'text-warning';
      case 'bonus': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-error/10 border-error/20';
      case 'recommended': return 'bg-warning/10 border-warning/20';
      case 'bonus': return 'bg-success/10 border-success/20';
      default: return 'bg-muted/10 border-border';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Panel Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Sparkles" size={20} className="text-primary" />
            <h3 className="font-semibold text-card-foreground">AI Suggestions</h3>
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              {filteredSuggestions?.length}
            </span>
          </div>
          {suggestions?.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              iconName="CheckCheck"
              onClick={onAcceptAll}
            >
              Accept All
            </Button>
          )}
        </div>

        {/* Match Score */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-card-foreground">Match Score</span>
            <span className="text-lg font-bold text-primary">{matchScore}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${matchScore}%` }}
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1">
          {[
            { key: 'all', label: 'All', count: suggestions?.length },
            { key: 'critical', label: 'Critical', count: suggestions?.filter(s => s?.priority === 'critical')?.length },
            { key: 'recommended', label: 'Recommended', count: suggestions?.filter(s => s?.priority === 'recommended')?.length },
            { key: 'bonus', label: 'Bonus', count: suggestions?.filter(s => s?.priority === 'bonus')?.length }
          ]?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setFilter(tab?.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                filter === tab?.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {tab?.label} ({tab?.count})
            </button>
          ))}
        </div>
      </div>
      {/* Suggestions List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Loader" size={32} className="text-primary animate-spin mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Generating AI suggestions...</p>
            </div>
          </div>
        ) : filteredSuggestions?.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="CheckCircle" size={32} className="text-success mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {filter === 'all' ? 'No suggestions available' : `No ${filter} suggestions`}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 p-4">
            {filteredSuggestions?.map((suggestion) => (
              <div 
                key={suggestion?.id}
                className={`border rounded-lg p-4 transition-all duration-200 ${getPriorityBg(suggestion?.priority)}`}
              >
                {/* Suggestion Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`text-xs font-medium uppercase tracking-wide ${getPriorityColor(suggestion?.priority)}`}>
                        {suggestion?.priority}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">{suggestion?.section}</span>
                    </div>
                    <h4 className="font-medium text-card-foreground text-sm">
                      {suggestion?.title}
                    </h4>
                  </div>
                  <button
                    onClick={() => toggleExpanded(suggestion?.id)}
                    className="p-1 hover:bg-muted rounded-md transition-colors duration-200"
                  >
                    <Icon 
                      name={expandedSuggestions?.has(suggestion?.id) ? "ChevronUp" : "ChevronDown"} 
                      size={16} 
                      className="text-muted-foreground" 
                    />
                  </button>
                </div>

                {/* Suggestion Content */}
                {expandedSuggestions?.has(suggestion?.id) && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {suggestion?.description}
                    </p>

                    {/* Before/After Comparison */}
                    <div className="space-y-3">
                      <div className="bg-error/5 border border-error/20 rounded-md p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="X" size={14} className="text-error" />
                          <span className="text-xs font-medium text-error uppercase tracking-wide">Before</span>
                        </div>
                        <p className="text-sm text-card-foreground font-mono">
                          {suggestion?.originalText}
                        </p>
                      </div>

                      <div className="bg-success/5 border border-success/20 rounded-md p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="Check" size={14} className="text-success" />
                          <span className="text-xs font-medium text-success uppercase tracking-wide">After</span>
                        </div>
                        <p className="text-sm text-card-foreground font-mono">
                          {suggestion?.improvedText}
                        </p>
                      </div>
                    </div>

                    {/* Keywords Highlighted */}
                    {suggestion?.keywords && suggestion?.keywords?.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-muted-foreground mb-2">
                          Keywords Added:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {suggestion?.keywords?.map((keyword, index) => (
                            <span 
                              key={index}
                              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2 pt-2">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Check"
                        onClick={() => onAcceptSuggestion(suggestion?.id)}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="X"
                        onClick={() => onRejectSuggestion(suggestion?.id)}
                      >
                        Reject
                      </Button>
                      <div className="flex-1" />
                      <span className="text-xs text-muted-foreground">
                        +{suggestion?.scoreImpact}% match
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuggestionPanel;